import React, { Component, ReactElement } from 'react';

import { LocatorAPI } from '@philips-emr/tws-components-api';
import { ModalType } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider, Value } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, expression, object, oneOf, oneOfType, string, Util } from '@philips-emr/tws-core/lib/component/prop-types';
import { ChangeEvent } from '@philips-emr/tws-core/lib/component/types';
import { classNames, difference, enumValues, getSlot, isDefined, isEmptyObject, isEqualShallow, isFunction, isUndefined, noop, propagateData } from '@philips-emr/tws-utils';

import { ValidationList } from '..';

import LocatorInput from './locator-input';

import './locator.css';

export interface LocatorProps {
  children: ReactElement[];
  className?: string;
  data?: Record<string, Value>[];
  displayProp: string;
  enabled: boolean;
  fragment: string;
  inputEnabled: boolean;
  modalType: ModalType;
  onBlur?: (event: FocusEvent) => void;
  onChange?: (event: ChangeEvent<Record<string, Value>>) => void;
  onFocus?: (event: FocusEvent) => void;
  onSort?: (options: Record<string, Value>) => void;
  placeholder: string;
  readOnly: boolean;
  uuid: string;
  value?: Record<string, Value>;
}

interface LocatorState {
  doSearch: boolean;
  record: Record<string, Value>;
  searchParameters: Record<string, Value>;
  valid: boolean;
}

const fieldsAreaAcceptedTypes = [
  ComponentType.FORM,
];

@AwesomeComponent({ type: ComponentType.DATA_ENTRY, api: LocatorAPI })
class Locator extends Component<LocatorProps, LocatorState> {

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    data: arrayOf(object).notEnumerable(),
    displayProp: string,
    enabled: bool,
    fragment: Util.withInfo(noop, { type: 'fragment' }),
    inputEnabled: bool,
    modalType: oneOf(enumValues(ModalType)),
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    onSort: eventListener,
    placeholder: expression,
    readOnly: bool.notEnumerable(),
    value: object.notEnumerable(),
  };

  static defaultProps = {
    data: [],
    displayProp: '',
    enabled: true,
    inputEnabled: true,
    modalType: ModalType.DEFAULT,
    placeholder: 'Sample label',
  };

  state = {
    doSearch: false,
    record: {},
    searchParameters: {},
    valid: true,
  };

  componentDidMount(): void {
    const { value } = this.props;

    if (value) {
      this.updateRecordFromValue();
    }
  }

  componentDidUpdate(oldProps: LocatorProps): void {
    const { value: oldValue = {}, data: oldData } = oldProps;
    const { data, value = {} } = this.props;

    if (Array.isArray(data) && difference(data, Array.isArray(oldData) ? oldData : []).length > 0) {
      return this.updateRecordFromData();
    }

    if (value !== oldValue && !isEqualShallow(value, oldValue)) {
      this.updateRecordFromValue();
    }
  }

  onRecordChange(): void {
    const { record } = this.state;
    const { onChange, value: oldValue } = this.props;

    if (isFunction(onChange)) {
      onChange(new ChangeEvent<Record<string, Value>>({ oldValue, value: record }));
    }
  }

  updateRecordFromData(): void {
    const { searchParameters } = this.state;
    const { data } = this.props;

    if (isEmptyObject(searchParameters)) {
      return;
    }

    const valid = Array.isArray(data) && data.length === 1;
    const changedData = data && valid ? data[0] : {};

    this.setState({ record: changedData, valid }, this.onRecordChange);
  }

  getNormalizedSearchParameters(record: Record<string, Value>): Record<string, Value> {
    const { searchParameters } = this.state;

    if (isEmptyObject(record)) {
      return searchParameters;
    }

    return Object.keys(searchParameters)
      .filter(key => key in record)
      .reduce((obj, key) => ({ ...obj, [key]: record[key] }), {});
  }

  updateRecordFromValue(): void {
    const { record } = this.state;
    const { value } = this.props;

    if (isUndefined(value) || isEmptyObject(value)) {
      this.setState({ record: {} }, this.onRecordChange);
      return;
    }

    const normalizedRecord = { ...record, ...value };

    this.setState({ record: normalizedRecord, searchParameters: this.getNormalizedSearchParameters(normalizedRecord) });
  }

  search(): void {
    const { doSearch, searchParameters } = this.state;
    const { onSort } = this.props;

    if (!doSearch) {
      return;
    }

    const params = Object.entries<Value>(searchParameters)
      .filter(([, value]) => isDefined(value) && value !== '')
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});

    const sort = (): void => {
      this.onRecordChange();

      if (isFunction(onSort)) {
        onSort({ params });
      }
    };

    this.setState({ doSearch: false, record: {}, valid: false }, sort);
  }

  onBlur = (event: FocusEvent): void => {
    const { onBlur } = this.props;

    this.search();

    if (isFunction(onBlur)) {
      onBlur(event);
    }
  };

  onFieldsChange = ({ entry, value }: ChangeEvent<string>): void => {
    if (!entry) {
      return;
    }

    const { searchParameters } = this.state;

    const newSearchParameters = { ...searchParameters, [entry]: value };

    this.setState({ doSearch: true, searchParameters: newSearchParameters });
  };

  onLocatorFieldSubmit = (record: Record<string, Value>): void => {
    this.setState({ record, valid: true }, this.onRecordChange);
  };

  getFieldsValue(): Record<string, Value> {
    const { readOnly } = this.props;
    const { searchParameters, record } = this.state;

    return readOnly ? record : { ...record, ...searchParameters };
  }

  getClassName(className?: string): string {
    const { valid } = this.state;

    return classNames({
      [`${className}`]: Boolean(className),
      'c-locator__invalid-field': !valid,
    });
  }

  renderValidation(): ReactElement | null {
    const { readOnly } = this.props;
    const { valid } = this.state;

    if (readOnly) {
      return null;
    }

    const validation = [{
      message: valid ? '' : 'tws.locator.result-not-found',
      name: 'search-validator',
      valid,
    }];

    return <ValidationList data={validation} />;
  }

  renderFields(): ReactElement[] {
    const { children, onFocus, readOnly } = this.props;

    const fieldsAreaChildren = getSlot(children, 'fields');

    const fieldProps = {
      className: this.getClassName('c-locator__field'),
      editing: !readOnly,
      onBlur: this.onBlur,
      onChange: this.onFieldsChange,
      onFocus,
      readOnly,
    };

    return propagateData(fieldsAreaChildren, this.getFieldsValue(), fieldProps);
  }

  renderLocatorField(): ReactElement {
    const {
      displayProp,
      enabled,
      fragment,
      inputEnabled,
      modalType,
      onFocus,
      placeholder,
      readOnly,
    } = this.props;

    const locatorInputProps = {
      className: this.getClassName(),
      displayProp,
      enabled,
      fragment,
      inputEnabled,
      modalType,
      onBlur: this.onBlur,
      onChange: this.onFieldsChange,
      onFocus,
      onSubmit: this.onLocatorFieldSubmit,
      placeholder,
      readOnly,
      value: this.getFieldsValue(),
    };

    return <LocatorInput {...locatorInputProps} />;
  }

  render(): ReactElement | null {
    const { className, uuid } = this.props;
    const Container = ContainerProvider.provide(this);

    if (!Container) {
      return null;
    }

    const wrapperClassName = classNames({
      [`${className}`]: Boolean(className),
    });

    return (
      <div className={wrapperClassName}>
        <Container acceptedTypes={fieldsAreaAcceptedTypes} className="c-locator" slot="fields" uuid={uuid} >
          {this.renderFields()}
          {this.renderLocatorField()}
        </Container>
        {this.renderValidation()}
      </div>
    );
  }
}

export default Locator;
